import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { reportsAPI } from '../services/api';
import { SIMULATE_CHANGED_EVENT } from '../services/simulate';
import api from '../services/api';
import InfoIconButton from '../components/InfoIconButton';
import { useReportsHubNavigation } from '../contexts/ReportsHubNavigationContext';
import './SchoolLowIncidentDisabilityReport.css';

function csvEscape(val) {
  if (val === null || val === undefined) return '';
  const s = String(val);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function cellStr(val) {
  if (val === null || val === undefined) return '';
  const s = String(val).trim();
  if (!s) return '';
  const parts = s.split(',').map((p) => p.trim());
  if (parts.every((p) => !p)) return '';
  return s;
}

function filterToken(val) {
  const s = cellStr(val);
  return s || '(blank)';
}

/** M/D/YYYY for display, filters, and CSV (matches School Referral Needed). */
function formatDate(val) {
  if (val === null || val === undefined) return '';
  const s = String(val).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
  return s;
}

function rowYmd(val) {
  if (val == null || val === '') return null;
  const m = String(val).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function rowFilterToken(row, key) {
  return filterToken(row[key]);
}

/** `filter: true` = multi-select; `filter: 'dateRange'` = Age 3 Start/Stop only */
const COLUMNS = [
  { key: 'client_name', label: 'Client', sortable: true, filter: true, screenOnly: true },
  { key: 'turns_three_date', label: 'Age 3', sortable: true, filter: 'dateRange' },
  { key: 'low_incident_dx', label: 'Diagnosis (low-incident)', sortable: true, filter: true },
  { key: 'sc_name', label: 'Staff', sortable: true, filter: true },
  { key: 'school_district', label: 'District', sortable: true, filter: true },
];

const MULTI_FILTER_KEYS = COLUMNS.filter((c) => c.filter === true).map((c) => c.key);
const AGE3_FILTER_KEY = 'turns_three_date';

function compare(a, b, key) {
  const va = a[key];
  const vb = b[key];
  if (va == null && vb == null) return 0;
  if (va == null) return 1;
  if (vb == null) return -1;
  if (key === 'turns_three_date') {
    const da = va ? new Date(va).getTime() : NaN;
    const db = vb ? new Date(vb).getTime() : NaN;
    if (!Number.isNaN(da) && !Number.isNaN(db)) return da - db;
    if (Number.isNaN(da) && Number.isNaN(db)) return 0;
    if (Number.isNaN(da)) return 1;
    if (Number.isNaN(db)) return -1;
  }
  return String(va).localeCompare(String(vb), undefined, { sensitivity: 'base' });
}

const REPORT_DESCRIPTION =
  'Open referrals only. Includes clients where Diagnosis Dx1, Dx2, or Dx3 is flagged Low incident disability in Settings → Diagnosis (auto-eligible). Age 3 is the third-birthday date; use the filter on that column to choose a Start and Stop date range (e.g. next three months). Other columns use checklist filters. Client names show on screen for staff use; Export CSV and Print omit client names for privacy when sharing outside the agency (e.g. with schools).';

export default function SchoolLowIncidentDisabilityReport({ embedded: _embedded = false, refreshKey = 0, tenantCode = null }) {
  const reportsHubNav = useReportsHubNavigation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [roleCode, setRoleCode] = useState(null);
  const [myStaffId, setMyStaffId] = useState(null);
  const [staffDisplayName, setStaffDisplayName] = useState(null);
  const [tenantCodeFromWhoami, setTenantCodeFromWhoami] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);

  const [sortKey, setSortKey] = useState('turns_three_date');
  const [sortDir, setSortDir] = useState('asc');
  const [columnFilters, setColumnFilters] = useState(() => {
    const init = {};
    MULTI_FILTER_KEYS.forEach((k) => {
      init[k] = new Set();
    });
    return init;
  });
  /** Applied Age 3 range (YYYY-MM-DD from date inputs); empty string = no bound */
  const [age3Filter, setAge3Filter] = useState({ from: '', to: '' });
  const [draftAge3From, setDraftAge3From] = useState('');
  const [draftAge3To, setDraftAge3To] = useState('');

  const [openFilterColumn, setOpenFilterColumn] = useState(null);
  const [draftFilter, setDraftFilter] = useState(new Set());
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 280 });

  const fetchWhoami = useCallback(async () => {
    try {
      const res = await api.get('/whoami');
      const code = res.data?.membership?.role_code || null;
      const sid = res.data?.user?.staff_id ?? null;
      setRoleCode(code);
      setMyStaffId(sid != null ? String(sid) : null);
      setStaffDisplayName(res.data?.staff_display_name ?? null);
      setTenantCodeFromWhoami(res.data?.tenant?.code ?? null);
    } catch {
      setRoleCode(null);
      setMyStaffId(null);
      setStaffDisplayName(null);
      setTenantCodeFromWhoami(null);
    }
  }, []);

  useEffect(() => {
    fetchWhoami();
  }, [fetchWhoami]);

  useEffect(() => {
    const handler = () => fetchWhoami();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchWhoami]);

  const runReport = useCallback(() => {
    setError(null);
    setLoading(true);
    reportsAPI
      .get('school-low-incident-disability', {})
      .then((res) => {
        setRows(res.data?.rows || []);
        setError(null);
      })
      .catch((err) => {
        setRows([]);
        setError(err.response?.data?.error || 'Failed to load report.');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    runReport();
  }, [runReport, refreshKey]);

  const handleSortClick = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const distinctForColumn = useCallback(
    (key) => {
      const set = new Set();
      rows.forEach((r) => set.add(rowFilterToken(r, key)));
      return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    },
    [rows]
  );

  const displayedRows = useMemo(() => {
    let out = rows;
    MULTI_FILTER_KEYS.forEach((key) => {
      const filt = columnFilters[key];
      if (filt && filt.size > 0) {
        out = out.filter((r) => filt.has(rowFilterToken(r, key)));
      }
    });
    const { from: af, to: at } = age3Filter;
    if (af || at) {
      out = out.filter((r) => {
        const ymd = rowYmd(r.turns_three_date);
        if (!ymd) return false;
        if (af && ymd < af) return false;
        if (at && ymd > at) return false;
        return true;
      });
    }
    out = [...out].sort((a, b) => {
      const cmp = compare(a, b, sortKey);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, columnFilters, age3Filter, sortKey, sortDir]);

  useEffect(() => {
    if (!filterAnchor || !openFilterColumn) return;
    const r = filterAnchor.getBoundingClientRect();
    const wide =
      openFilterColumn === 'low_incident_dx' ||
      openFilterColumn === 'school_district' ||
      openFilterColumn === AGE3_FILTER_KEY;
    const width = openFilterColumn === AGE3_FILTER_KEY ? 300 : wide ? 420 : 240;
    const padding = 12;
    let left = r.left;
    if (left + width > window.innerWidth - padding) {
      left = window.innerWidth - width - padding;
    }
    if (left < padding) left = padding;
    let top = r.bottom;
    const maxHeight = 320;
    if (top + maxHeight > window.innerHeight - padding) {
      top = Math.max(padding, r.top - maxHeight);
    }
    setPopoverPosition({ top, left, width });
  }, [filterAnchor, openFilterColumn]);

  const openFilter = (colKey, ev) => {
    ev.stopPropagation();
    setFilterAnchor(ev.currentTarget);
    if (colKey === AGE3_FILTER_KEY) {
      setDraftAge3From(age3Filter.from || '');
      setDraftAge3To(age3Filter.to || '');
    } else {
      setDraftFilter(new Set(columnFilters[colKey] || []));
    }
    setOpenFilterColumn(colKey);
  };

  const applyFilter = () => {
    if (!openFilterColumn) return;
    if (openFilterColumn === AGE3_FILTER_KEY) {
      setAge3Filter({ from: draftAge3From.trim(), to: draftAge3To.trim() });
    } else {
      setColumnFilters((prev) => ({
        ...prev,
        [openFilterColumn]: new Set(draftFilter),
      }));
    }
    setOpenFilterColumn(null);
    setFilterAnchor(null);
  };

  const clearFilter = () => {
    if (!openFilterColumn) return;
    if (openFilterColumn === AGE3_FILTER_KEY) {
      setDraftAge3From('');
      setDraftAge3To('');
      setAge3Filter({ from: '', to: '' });
    } else {
      setDraftFilter(new Set());
      setColumnFilters((prev) => ({
        ...prev,
        [openFilterColumn]: new Set(),
      }));
    }
    setOpenFilterColumn(null);
    setFilterAnchor(null);
  };

  const toggleDraft = (val) => {
    setDraftFilter((prev) => {
      const next = new Set(prev);
      if (next.has(val)) next.delete(val);
      else next.add(val);
      return next;
    });
  };

  const downloadCsv = () => {
    const note = 'NOTE: Client names are omitted from this export for privacy when sharing outside the agency.';
    const headers = ['turns_three_date', 'low_incident_dx', 'sc_name', 'school_district'];
    const lines = [
      note,
      headers.join(','),
      ...displayedRows.map((r) =>
        headers
          .map((h) => (h === 'turns_three_date' ? csvEscape(formatDate(r[h])) : csvEscape(r[h])))
          .join(',')
      ),
    ];
    const csv = lines.join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'school-low-incident-disability.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handlePrint = () => {
    window.print();
  };

  const resetFilters = () => {
    const init = {};
    MULTI_FILTER_KEYS.forEach((k) => {
      init[k] = new Set();
    });
    setColumnFilters(init);
    setAge3Filter({ from: '', to: '' });
    setDraftAge3From('');
    setDraftAge3To('');
  };

  const isSc = roleCode === 'SERVICE_COORDINATOR';
  const titleSuffix = tenantCode || tenantCodeFromWhoami ? ` — ${tenantCode || tenantCodeFromWhoami}` : '';
  const scDisplayName = staffDisplayName || (myStaffId ? `ID ${myStaffId}` : null);

  const activeFilterSummary = useMemo(() => {
    const parts = [];
    if (age3Filter.from || age3Filter.to) {
      const start = age3Filter.from ? formatDate(age3Filter.from) : '…';
      const stop = age3Filter.to ? formatDate(age3Filter.to) : '…';
      parts.push(`Age 3: ${start} – ${stop}`);
    }
    MULTI_FILTER_KEYS.forEach((key) => {
      const filt = columnFilters[key];
      if (filt && filt.size > 0) {
        const col = COLUMNS.find((c) => c.key === key);
        const label = col ? col.label : key;
        const vals = [...filt].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
        parts.push(`${label}: ${vals.join(', ')}`);
      }
    });
    return parts;
  }, [columnFilters, age3Filter]);

  const filterPopover =
    openFilterColumn &&
    createPortal(
      <div
        className={`school-lid-filter-popover ${openFilterColumn === 'low_incident_dx' || openFilterColumn === 'school_district' ? 'school-lid-filter-popover-wide' : ''}`}
        style={{
          position: 'fixed',
          top: `${popoverPosition.top}px`,
          left: `${popoverPosition.left}px`,
          width: `${popoverPosition.width}px`,
        }}
        role="dialog"
        aria-label={`Filter ${openFilterColumn}`}
      >
        {openFilterColumn === AGE3_FILTER_KEY ? (
          <>
            <h3 className="school-lid-filter-popover-title">Age 3</h3>
            <div className="school-lid-date-range-fields">
              <label className="school-lid-date-range-label">
                <span>Start</span>
                <input
                  type="date"
                  className="school-lid-date-range-input"
                  value={draftAge3From}
                  onChange={(e) => setDraftAge3From(e.target.value)}
                />
              </label>
              <label className="school-lid-date-range-label">
                <span>Stop</span>
                <input
                  type="date"
                  className="school-lid-date-range-input"
                  value={draftAge3To}
                  onChange={(e) => setDraftAge3To(e.target.value)}
                />
              </label>
            </div>
            <p className="school-lid-date-range-hint">Include third birthdays on or after Start and on or before Stop (leave either blank for no limit).</p>
            <div className="school-lid-filter-popover-actions">
              <button type="button" className="btn btn-secondary" onClick={clearFilter}>
                Clear
              </button>
              <button type="button" className="btn btn-primary" onClick={applyFilter}>
                Apply
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="school-lid-filter-popover-actions">
              <button type="button" className="btn btn-secondary" onClick={clearFilter}>
                Clear
              </button>
              <button type="button" className="btn btn-primary" onClick={applyFilter}>
                Apply
              </button>
            </div>
            <div className="school-lid-filter-popover-list">
              {distinctForColumn(openFilterColumn).map((val) => (
                <label key={val} className="school-lid-filter-check">
                  <input type="checkbox" checked={draftFilter.has(val)} onChange={() => toggleDraft(val)} />
                  <span>{val}</span>
                </label>
              ))}
            </div>
            <div className="school-lid-filter-popover-actions">
              <button type="button" className="btn btn-secondary" onClick={clearFilter}>
                Clear
              </button>
              <button type="button" className="btn btn-primary" onClick={applyFilter}>
                Apply
              </button>
            </div>
          </>
        )}
      </div>,
      document.body
    );

  return (
    <div className="school-lid-report">
      <p className="school-lid-print-notice" aria-hidden="true">
        Client names are omitted from this document for student privacy (school-facing copy).
      </p>

      <div className="school-lid-toolbar">
        <div className="school-lid-toolbar-left">
          <h2 className="school-lid-title">
            School Low Incident Disability
            {titleSuffix ? <span className="school-lid-title-suffix">{titleSuffix}</span> : null}
          </h2>
          <InfoIconButton
            onClick={() => setInfoOpen((v) => !v)}
            ariaExpanded={infoOpen}
            ariaControls="school-lid-info"
            ariaLabel="Show report description"
            ariaLabelExpanded="Hide report description"
            className="school-lid-info-icon"
          />
        </div>
        <div className="school-lid-toolbar-right school-lid-actions">
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => {
              if (reportsHubNav?.closeActiveReport) {
                reportsHubNav.closeActiveReport('School / ES');
                return;
              }
              setRows([]);
              setError(null);
              resetFilters();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary school-lid-download-btn"
            onClick={downloadCsv}
            disabled={displayedRows.length === 0}
          >
            Export CSV
          </button>
          <button
            type="button"
            className="btn btn-primary school-lid-print-btn"
            onClick={handlePrint}
            disabled={displayedRows.length === 0}
          >
            Print
          </button>
        </div>
      </div>
      {infoOpen && (
        <p id="school-lid-info" className="school-lid-info-text">
          {REPORT_DESCRIPTION}
        </p>
      )}
      {isSc && scDisplayName && (
        <p className="school-lid-context" aria-live="polite">
          Staff / Service Coordinator: {scDisplayName}
        </p>
      )}

      {activeFilterSummary.length > 0 && (
        <p className="school-lid-filter-summary no-print">
          Filters: {activeFilterSummary.join(' · ')}
        </p>
      )}

      {loading && (
        <p className="school-lid-loading" aria-live="polite">
          Loading…
        </p>
      )}
      {error && (
        <div className="school-lid-error" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && (
        <div className="school-lid-results">
          <p className="school-lid-count">
            {displayedRows.length} row{displayedRows.length === 1 ? '' : 's'}
            {displayedRows.length !== rows.length ? ` (of ${rows.length} loaded)` : ''}
          </p>
          <div className="school-lid-table-wrap">
            <table className="school-lid-table">
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.key}
                      className={`${col.sortable ? 'school-lid-sortable' : ''} ${col.numeric ? 'school-lid-cell-num' : ''} ${col.screenOnly ? 'school-lid-col-screen-only' : ''}`.trim()}
                    >
                      <div className="school-lid-header-cell">
                        <button
                          type="button"
                          className="school-lid-header-sort-btn"
                          onClick={() => col.sortable && handleSortClick(col.key)}
                          disabled={!col.sortable}
                        >
                          <span className="school-lid-header-label">{col.label}</span>
                          {col.sortable && sortKey === col.key && (
                            <span className="school-lid-sort-arrow" aria-hidden="true">
                              {sortDir === 'asc' ? ' ▲' : ' ▼'}
                            </span>
                          )}
                        </button>
                        {col.filter && (
                          <span className="school-lid-header-filter-wrap" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className={[
                                'school-lid-filter-trigger',
                                col.key === AGE3_FILTER_KEY
                                  ? age3Filter.from || age3Filter.to
                                    ? 'is-active'
                                    : null
                                  : (columnFilters[col.key]?.size || 0) > 0
                                    ? 'is-active'
                                    : null,
                              ]
                                .filter(Boolean)
                                .join(' ')}
                              aria-label={`Filter ${col.label}`}
                              onClick={(e) => openFilter(col.key, e)}
                            >
                              <svg className="school-lid-filter-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M4 6H20L14 13V18L10 20V13L4 6Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {displayedRows.map((r) => (
                  <tr key={r.referral_id}>
                    {COLUMNS.map((col) => (
                      <td
                        key={col.key}
                        className={`${col.numeric ? 'school-lid-cell-num' : ''} ${col.screenOnly ? 'school-lid-col-screen-only' : ''}`.trim()}
                      >
                        {col.key === 'turns_three_date'
                          ? formatDate(r.turns_three_date) || '—'
                          : cellStr(r[col.key]) || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {filterPopover}
    </div>
  );
}
